import * as THREE from "three";
import {
  DEFAULT_COLOR,
  DEFAULT_INTENSITY,
  DEFAULT_THICKNESS,
  type TubeData,
} from "./types";

const TUBE_SEGMENTS_PER_UNIT = 10;
const RADIAL_SEGMENTS = 14;
const MIN_POINTS_FOR_MESH = 2;
const WARM_TINT = new THREE.Color(1.0, 0.92, 0.78);
const COOL_TINT = new THREE.Color(1.0, 1.0, 1.0);

/**
 * A single neon tube. Holds its control points, color, thickness, and the
 * composite mesh used to render the glowing core. Bloom post-processing in the
 * scene turns the bright emissive material into the soft halo.
 */
export class NeonTube {
  readonly id: string;
  readonly group: THREE.Group;
  points: THREE.Vector3[];
  color: THREE.Color;
  thickness: number;
  intensity: number;
  warm: boolean;

  private coreMesh: THREE.Mesh | null = null;
  private shellMesh: THREE.Mesh | null = null;
  private endCaps: THREE.Mesh[] = [];
  private coreMat: THREE.MeshBasicMaterial;
  private shellMat: THREE.MeshBasicMaterial;
  private capMat: THREE.MeshStandardMaterial;
  private pointLights: THREE.PointLight[] = [];
  // Held separately from the live material color so the per-frame flicker can
  // re-multiply without the base drifting on each tick.
  private coreBase = new THREE.Color();
  private shellBase = new THREE.Color();
  private flickerPhase = Math.random() * Math.PI * 2;
  private flickerSeed = 0.4 + Math.random() * 1.2;
  private cachedLength = 0;

  constructor(init: Partial<TubeData> = {}) {
    this.id = init.id ?? crypto.randomUUID();
    this.color = new THREE.Color(init.color ?? DEFAULT_COLOR);
    this.thickness = init.thickness ?? DEFAULT_THICKNESS;
    this.intensity = init.intensity ?? DEFAULT_INTENSITY;
    this.warm = init.warm ?? false;
    this.points = (init.points ?? []).map((p) => new THREE.Vector3(p[0], p[1], p[2]));

    this.group = new THREE.Group();
    this.group.name = `neon-tube-${this.id}`;
    this.group.userData.tubeId = this.id;

    this.coreMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      toneMapped: false,
      transparent: true,
    });
    this.shellMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      toneMapped: false,
      transparent: true,
      opacity: 0.22,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.capMat = new THREE.MeshStandardMaterial({
      color: 0x16161c,
      metalness: 0.55,
      roughness: 0.45,
    });
    this.applyColor();
  }

  addPoint(p: THREE.Vector3): void {
    this.points.push(p.clone());
    this.rebuildMesh();
  }

  setPoint(index: number, p: THREE.Vector3): void {
    if (index < 0 || index >= this.points.length) return;
    this.points[index].copy(p);
    this.rebuildMesh();
  }

  removePoint(index: number): void {
    if (index < 0 || index >= this.points.length) return;
    this.points.splice(index, 1);
    this.rebuildMesh();
  }

  setColor(hex: string): void {
    this.color.set(hex);
    this.applyColor();
  }

  setThickness(t: number): void {
    this.thickness = t;
    this.rebuildMesh();
  }

  setIntensity(i: number): void {
    this.intensity = i;
    this.applyColor();
    this.pointLights.forEach((l) => {
      l.intensity = this.intensity * 0.9;
    });
  }

  setWarm(warm: boolean): void {
    this.warm = warm;
    this.applyColor();
  }

  /** Curve length in scene units. Cached on rebuildMesh. */
  get length(): number {
    return this.cachedLength;
  }

  /** Is the tube renderable (has enough points to form a curve)? */
  get renderable(): boolean {
    return this.points.length >= MIN_POINTS_FOR_MESH;
  }

  /**
   * Per-frame flicker tick. Subtly modulates the core/shell brightness with
   * a slow noise wobble so the tube feels alive instead of static. Runs cheap:
   * one sin call per material per frame.
   */
  tickFlicker(time: number): void {
    if (!this.coreMesh) return;
    const t = time * 0.001 * this.flickerSeed + this.flickerPhase;
    // Two sins beat together for an irregular, non-periodic-looking pulse.
    const wobble = 1 + (Math.sin(t * 6.7) * 0.012 + Math.sin(t * 2.3) * 0.018);
    this.coreMat.color.copy(this.coreBase).multiplyScalar(wobble);
    this.shellMat.color.copy(this.shellBase).multiplyScalar(wobble);
  }

  /**
   * Rebuilds the TubeGeometry from the current control points. Cheap enough
   * to call on every drag frame for tubes under ~200 points.
   */
  rebuildMesh(): void {
    this.disposeMeshes();
    if (!this.renderable) {
      this.cachedLength = 0;
      this.updatePointLights();
      return;
    }

    const curve = new THREE.CatmullRomCurve3(this.points, false, "catmullrom", 0.5);
    this.cachedLength = curve.getLength();
    const tubularSegments = Math.max(8, Math.ceil(this.cachedLength * TUBE_SEGMENTS_PER_UNIT));

    const coreGeom = new THREE.TubeGeometry(curve, tubularSegments, this.thickness, RADIAL_SEGMENTS, false);
    const shellGeom = new THREE.TubeGeometry(curve, tubularSegments, this.thickness * 1.7, RADIAL_SEGMENTS, false);

    this.coreMesh = new THREE.Mesh(coreGeom, this.coreMat);
    this.shellMesh = new THREE.Mesh(shellGeom, this.shellMat);
    this.coreMesh.userData.tubeId = this.id;
    this.shellMesh.userData.tubeId = this.id;
    this.coreMesh.renderOrder = 1;
    this.shellMesh.renderOrder = 0;

    this.group.add(this.shellMesh);
    this.group.add(this.coreMesh);

    this.endCaps = [this.makeEndCap(curve, 0), this.makeEndCap(curve, 1)];
    this.endCaps.forEach((c) => this.group.add(c));

    this.updatePointLights();
  }

  /**
   * Build a dark "electrode" cap at the start (t=0) or end (t=1) of the curve.
   * The cap is a short cylinder oriented along the curve tangent, slightly
   * wider than the tube so it visually anchors the glowing glass to a real
   * piece of hardware instead of letting the bright stroke float in space.
   */
  private makeEndCap(curve: THREE.CatmullRomCurve3, t: 0 | 1): THREE.Mesh {
    const radius = this.thickness * 1.35;
    const length = this.thickness * 2.2;
    const geom = new THREE.CylinderGeometry(radius, radius, length, 16, 1, false);
    const mesh = new THREE.Mesh(geom, this.capMat);

    const position = curve.getPointAt(t);
    const tangent = curve.getTangentAt(t).normalize();

    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), tangent);
    mesh.quaternion.copy(quat);

    const outward = t === 1 ? 1 : -1;
    mesh.position.copy(position).addScaledVector(tangent, outward * length * 0.35);

    mesh.userData.tubeId = this.id;
    mesh.renderOrder = 2;
    return mesh;
  }

  private updatePointLights(): void {
    const target = this.renderable ? Math.min(4, Math.max(1, Math.floor(this.points.length / 3))) : 0;
    while (this.pointLights.length > target) {
      const l = this.pointLights.pop()!;
      this.group.remove(l);
    }
    while (this.pointLights.length < target) {
      const l = new THREE.PointLight(this.color, this.intensity * 0.9, 6, 1.6);
      this.group.add(l);
      this.pointLights.push(l);
    }
    if (target > 0) {
      const curve = new THREE.CatmullRomCurve3(this.points, false, "catmullrom", 0.5);
      for (let i = 0; i < this.pointLights.length; i++) {
        const t = (i + 0.5) / this.pointLights.length;
        this.pointLights[i].position.copy(curve.getPointAt(t));
        this.pointLights[i].color.copy(this.color);
      }
    }
  }

  private applyColor(): void {
    const boost = 0.55 + this.intensity * 0.9;
    this.coreBase.copy(this.color).multiplyScalar(boost * 1.15);
    this.shellBase.copy(this.color).multiplyScalar(boost * 0.55);
    // Whiten the core only at higher intensities. Warm tubes lerp toward a
    // peachy-white (phosphor-coated tubes look like this); cool stays neutral.
    const whiten = THREE.MathUtils.clamp((this.intensity - 1.2) * 0.35, 0, 0.45);
    if (whiten > 0) {
      const tint = this.warm ? WARM_TINT : COOL_TINT;
      this.coreBase.lerp(tint, whiten);
    }
    this.coreMat.color.copy(this.coreBase);
    this.shellMat.color.copy(this.shellBase);
  }

  private disposeMeshes(): void {
    if (this.coreMesh) {
      this.group.remove(this.coreMesh);
      (this.coreMesh.geometry as THREE.BufferGeometry).dispose();
      this.coreMesh = null;
    }
    if (this.shellMesh) {
      this.group.remove(this.shellMesh);
      (this.shellMesh.geometry as THREE.BufferGeometry).dispose();
      this.shellMesh = null;
    }
    this.endCaps.forEach((c) => {
      this.group.remove(c);
      (c.geometry as THREE.BufferGeometry).dispose();
    });
    this.endCaps = [];
  }

  dispose(): void {
    this.disposeMeshes();
    this.coreMat.dispose();
    this.shellMat.dispose();
    this.capMat.dispose();
    this.pointLights.forEach((l) => this.group.remove(l));
    this.pointLights = [];
  }

  toData(): TubeData {
    return {
      id: this.id,
      color: `#${this.color.getHexString()}`,
      thickness: Number(this.thickness.toFixed(3)),
      intensity: Number(this.intensity.toFixed(3)),
      warm: this.warm || undefined,
      points: this.points.map((p) => [
        Number(p.x.toFixed(3)),
        Number(p.y.toFixed(3)),
        Number(p.z.toFixed(3)),
      ]),
    };
  }
}
